/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Button from '../button/Button';
import EntertainmentOption from '../entertainmentOption/EntertainmentOption';
import Separator from '../separator/Separator';
import NewDialog from '../newDialog/NewDialog';
import Dialog from '../dialog/Dialog';
import {
  isSplash,
  isCurrentTimeAfterEvent,
  getDateLabel,
  isNowBetweenLateBookingTimeRange,
  entertainmentType,
  getIfAllowedToBookEveningShowSlot,
  IsSelectedEventExpired,
  areAllCheckedSlotsAlreadyBooked,
  getArrayWithUniqueElements,
  getClashedBookedEntertainments,
  getEventsOnThisDay,
  getRemovableEntertainmentDateLabel,
  getIsChecked,
  getSlotColor,
  checkIsConfigNeedsToBeRefreshed,
  redirectUserToSchedulePageWithReload,
  getNumOfBookedEveningShowsOnCertainDay,
  filterSlotsByBackstop,
} from '../../utils';
import { virtualPageView } from '../../googleTagManager';
import './EntertainmentOptions.scss';
import DialogMessage from '../dialogMessage/DialogMessage';
import GuestSelector from '../guestSelector/GuestSelector';

const getOptionDisabled = (
  checked,
  areSpacesAvailable,
  singleDate = '',
  duration = 0,
  isAlreadyBooked,
  isAllowedToBookEveningShow,
) => !checked && !isAlreadyBooked && (!areSpacesAvailable || isCurrentTimeAfterEvent(singleDate, duration) || !isAllowedToBookEveningShow);

const handleCancel = (setChecked, removeAddon, bookedSlotsOfThisEvent, checked) => {
  if (checked.length > 0 && bookedSlotsOfThisEvent.length === 0) setChecked([]);
  if (checked.length > 0 && bookedSlotsOfThisEvent.length > 0) {
    setChecked([]);
    removeAddon();
  }
};

const isAllowedBookMoreEntertainments = (
  singleDate,
  duration,
  alreadyBookedEntertainmentsNumber,
  maxAllowedShowBookingSlots,
  allowLateBooking,
  lateBookingWindowMinutes,
) => (!allowLateBooking
  ? alreadyBookedEntertainmentsNumber < maxAllowedShowBookingSlots
  : isNowBetweenLateBookingTimeRange(singleDate, duration, lateBookingWindowMinutes));

const onOptionClick = (
  setChecked,
  disabled,
  code,
  setEntertainmentDialogData,
  checked,
  singleDate,
  duration,
  alreadyBookedEntertainmentsNumber,
  maxAllowedShowBookingSlots,
  allowLateBooking,
  clashedEvents = [],
  setClashedEvents,
  setOverlapsDialogData,
  lateBookingWindowMinutes,
  preventOverlapMandatory,
  setGuestSelectorDialogData,
  isPerPersonActivity,
  clashedEventsForTheSelectedEvent,
  showReplacePopup,
) => {
  if (disabled || checked.filter(({ slotId }) => slotId === code).length > 0) return;
  const isClashedEvents = clashedEvents.length > 0;
  const isMandatoryOverlapAndClashed = preventOverlapMandatory && isClashedEvents;
  const isNotMandatoryOverlapOrNotClashed = !preventOverlapMandatory || !isClashedEvents;
  const isNotMandatoryOverlapButClashed = !preventOverlapMandatory && isClashedEvents;

  if (isClashedEvents) setClashedEvents(clashedEvents);
  if (isMandatoryOverlapAndClashed) setOverlapsDialogData({ show: true, relatedSlotId: code });
  if (!isAllowedBookMoreEntertainments(
    singleDate,
    duration,
    alreadyBookedEntertainmentsNumber,
    maxAllowedShowBookingSlots,
    allowLateBooking,
    lateBookingWindowMinutes,
  )) {
    if (isNotMandatoryOverlapOrNotClashed && !(alreadyBookedEntertainmentsNumber < maxAllowedShowBookingSlots)) {
      setEntertainmentDialogData({ show: true, relatedSlotId: code });
      return;
    }
    if ((!clashedEventsForTheSelectedEvent || clashedEventsForTheSelectedEvent.length === 0) && !showReplacePopup) return;
    virtualPageView('plan my day', 'book entertainment', '/ga-virtual/plan-my-day/book-entertainment/maximum-bookings-reached-notice');
  }
  if (isNotMandatoryOverlapButClashed) setOverlapsDialogData({ show: true, relatedSlotId: code });
  if (isPerPersonActivity) {
    if (!isMandatoryOverlapAndClashed) setGuestSelectorDialogData({ show: true, selectedSlotId: code });
  } else {
    setChecked([{ slotId: code, guestNumber: 1 }]);
  }
};

const getEntertainmentDialogMessage = (showReplacePopup, clashedEventsForTheSelectedEvent) => {
  const messages = ['Sorry you have reached the maximum number of bookings for this event!'];
  if (clashedEventsForTheSelectedEvent
    && clashedEventsForTheSelectedEvent.length > 0) messages.push('Please note that this new session overlaps with another booking that you already have.');
  if (showReplacePopup) messages.push('Choosing a new time will replace your existing session:');
  return messages;
};

const onPositiveEntertainmentDialogButtonClick = (showReplacePopup, setEntertainmentDialogData, saveAddon, setClashedEvents, { relatedSlotId }) => {
  setClashedEvents([]);
  if (showReplacePopup) saveAddon([{ slotId: relatedSlotId }], true);
  setEntertainmentDialogData({ show: false, relatedSlotId: null });
};

const onNegativeEntertainmentDialogButtonClick = (setEntertainmentDialogData, setClashedEvents) => {
  setClashedEvents([]);
  setEntertainmentDialogData({ show: false, relatedSlotId: null });
};

const getIsOnlyOneBookedEntertainmentInThePast = (isMoreThanOneBookedEntertainmentsOfThisCategory, alreadyBookedEntertainmentsOfCategory) => {
  if (isMoreThanOneBookedEntertainmentsOfThisCategory) return false;
  const [onlyOneBooked = {}] = alreadyBookedEntertainmentsOfCategory;
  const { Time = '' } = onlyOneBooked;
  return moment(Time).isBefore(moment());
};

const getClashedDialogMessage = (clashedEventsForTheSelectedEvent, preventOverlapMandatory) => {
  const isOnlyOneClashedEvent = clashedEventsForTheSelectedEvent.length === 1;
  if (preventOverlapMandatory) {
    if (isOnlyOneClashedEvent) {
      const [{ name: clashedName, startTime }] = clashedEventsForTheSelectedEvent;
      return [`This overlaps with booking ${clashedName} at ${startTime}.`, 'Do you wish to cancel that booking?'];
    }
    return ['This clashes with a number of other items.', 'You will need to cancel those before you can book this one.'];
  }
  if (isOnlyOneClashedEvent) {
    const [{ name: clashedName, startTime }] = clashedEventsForTheSelectedEvent;
    return [`This overlaps with booking ${clashedName} at ${startTime}.`, 'Are you sure you wish to book this one?'];
  }
  return ['This clashes with a number of other items.'];
};

const onPositiveClashedDialogButtonClick = async (
  preventOverlapMandatory,
  setClashedEvents,
  setOverlapsDialogData,
  clashedEventsForTheSelectedEvent,
  needToRemoveTheOtherClashedEvent,
  removeClashedEvent,
  setChecked,
  checked,
  needToShowEntertainmentsDialog,
  setEntertainmentDialogData,
  { relatedSlotId },
  isPerPersonActivity,
  setGuestSelectorDialogData,
  getBookedEntertainments,
  resId,
  siteCode,
) => {
  setClashedEvents([]);
  setOverlapsDialogData({ show: false, relatedSlotId: null });
  if (needToRemoveTheOtherClashedEvent && clashedEventsForTheSelectedEvent.length === 1) {
    await removeClashedEvent(clashedEventsForTheSelectedEvent[0]);
    getBookedEntertainments(resId, siteCode);
    if (needToShowEntertainmentsDialog) setEntertainmentDialogData({ show: true, relatedSlotId });
    if (isPerPersonActivity) setGuestSelectorDialogData({ show: true, selectedSlotId: relatedSlotId });
    return;
  }
  if (preventOverlapMandatory) {
    if (isPerPersonActivity) setGuestSelectorDialogData({ show: true, selectedSlotId: relatedSlotId });
    setChecked(checked.slice(0, -1));
  }
};

const onNegativeClashedDialogButtonClick = (
  setClashedEvents,
  setOverlapsDialogData,
  setChecked,
  checked,
  preventOverlapMandatory,
  clashedEventsForTheSelectedEvent,
) => {
  const isClashedEvents = clashedEventsForTheSelectedEvent.length > 0;
  const isMandatoryOverlapAndClashed = preventOverlapMandatory && isClashedEvents;
  const isNotMandatoryOverlapButClashed = !preventOverlapMandatory && isClashedEvents;
  setClashedEvents([]);
  if (isMandatoryOverlapAndClashed || isNotMandatoryOverlapButClashed) setChecked(checked.slice(0, -1));
  setOverlapsDialogData({ show: false, relatedSlotId: null });
};

const getMappedBookedSlots = (bookedEvents, bookedSlotsOfThisEvent) => bookedEvents
  .filter(({ id: timeSlotId }) => bookedSlotsOfThisEvent.includes(parseInt(timeSlotId, 10)))
  .map(({ id: timeSlotId, numOfGuests: guestNumber = 1 }) => ({ slotId: parseInt(timeSlotId, 10), guestNumber }));

const getIsBookButtonDisabled = (checked,
  bookedSlotsOfThisEvent,
  isChristmasEventBooked,
  isRestrictedShowBooked) => ((isChristmasEventBooked || checked.length === 0 || isRestrictedShowBooked) || (areAllCheckedSlotsAlreadyBooked(bookedSlotsOfThisEvent, checked)));

const getShowCancelButton = (bookedSlotsOfThisEvent, options) => !IsSelectedEventExpired(bookedSlotsOfThisEvent, options);

const checkOneChristmasEventIsBooked = (selectedActivityId, allBookedEntertainments, xmasCollectionActId, xmasExperienceActId) => {
  if (selectedActivityId === xmasCollectionActId || selectedActivityId === xmasExperienceActId) {
    const xmasEntertainments = allBookedEntertainments.filter(
      ({ ActivityId: activityId }) => activityId === xmasCollectionActId || activityId === xmasExperienceActId,
    );
    return xmasEntertainments.length > 0;
  }
  return false;
};

const checkOneRestrictedShowIsBooked = (selectedActivityId, allBookedEntertainments, showRestrictId1, showRestrictId2) => {
  if (selectedActivityId === showRestrictId1 || selectedActivityId === showRestrictId2) {
    const restrictedActs = allBookedEntertainments.filter(
      ({ ActivityId: activityId }) => activityId === showRestrictId1 || activityId === showRestrictId2,
    );
    return restrictedActs.length > 0;
  }
  return false;
};

const onBookButton = (
  refreshTimeInterval,
  lastConfigCheckDateTime,
  saveAddon,
  checked,
  setShowReloadIsNecessaryDialog,
) => {
  const isReloadNecessary = checkIsConfigNeedsToBeRefreshed(lastConfigCheckDateTime, refreshTimeInterval);
  if (isReloadNecessary) {
    setShowReloadIsNecessaryDialog(true);
    return;
  }
  saveAddon(checked);
};

const getMaxNumOfGuests = (options = [], selectedSlotId, maxQuantity) => {
  const selectedSlot = options.find(({ code }) => code === selectedSlotId);
  if (!selectedSlot) return maxQuantity;
  const { remainingCapacity } = selectedSlot;
  if (!remainingCapacity) return maxQuantity;
  return Math.min(maxQuantity, remainingCapacity);
};

const EntertainmentOptions = ({
  title = 'Setup eating time...',
  separatorLabel = 'Dinning room closed for deep clean',
  options = [],
  saveAddon = () => { },
  removeAddon = () => { },
  removeClashedEvent = () => { },
  bookedSlotsOfThisEvent = [],
  maxAllowedShowBookingSlots = 1,
  bookedEvents = [],
  quantityToBook = 1,
  allBookedEntertainments = [],
  lateBookingWindowMinutes,
  overlapCheckPoolItems,
  overlapAllowedMinutes,
  allowLateBooking,
  preventOverlapMandatory,
  xmasCollectionActId,
  xmasExperienceActId,
  selectedEntertainment = {},
  isItTargetCategoryForEveningShow = false,
  eveningShowRestrictionStartDate,
  eveningShowRestrictionEndDate,
  eveningShowRestrictionStartTime = '',
  eveningShowRestrictionEndTime = '',
  additionalEveningShowTime,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  isPerPersonActivity,
  maxQuantity,
  setSelectedNumOfGuests,
  backstopDate,
  getBookedEntertainments,
  resId,
  siteCode,
  showRestrictId1,
  showRestrictId2,
}) => {
  const { activityId: selectedActivityId, date: activityDate } = selectedEntertainment;
  const [checked, setChecked] = useState([]);
  const [clashedEventsForTheSelectedEvent, setClashedEvents] = useState([]);
  const [entertainmentDialogData, setEntertainmentDialogData] = useState({ show: false, relatedSlotId: null });
  const [overlapsDialogData, setOverlapsDialogData] = useState({ show: false, relatedSlotId: null });
  const [showReloadIsNecessaryDialog, setShowReloadIsNecessaryDialog] = useState(false);
  const defaultGuestSelectorHookValue = { show: false, selectedSlotId: null };
  const [guestSelectorDialogData, setGuestSelectorDialogData] = useState(defaultGuestSelectorHookValue);
  const { show: showGuestSelectorDialog, selectedSlotId: selectedSlotIdOfGuestSelector } = guestSelectorDialogData;
  const optionsLength = options.length;
  const bookedSlotsOfThisEventString = bookedSlotsOfThisEvent.sort().join();
  const alreadyBookedEntertainmentsOfCategory = allBookedEntertainments.filter(
    ({ ActivityId: activityId, ActivityName: name }) => selectedActivityId === activityId && !isSplash(name),
  );
  const isChristmasEventBooked = checkOneChristmasEventIsBooked(selectedActivityId, allBookedEntertainments, xmasCollectionActId, xmasExperienceActId);
  const isRestrictedShowBooked = checkOneRestrictedShowIsBooked(selectedActivityId, allBookedEntertainments, showRestrictId1, showRestrictId2);
  const alreadyBookedEntertainmentsNumber = alreadyBookedEntertainmentsOfCategory && alreadyBookedEntertainmentsOfCategory.length > 0
    ? alreadyBookedEntertainmentsOfCategory.length
    : 0;

  const isMoreThanOneBookedEntertainmentsOfThisCategory = alreadyBookedEntertainmentsNumber > 1;
  const isOnlyOneEntertainmentBookedInThePast = getIsOnlyOneBookedEntertainmentInThePast(
    isMoreThanOneBookedEntertainmentsOfThisCategory, alreadyBookedEntertainmentsOfCategory,
  );
  const showReplacePopup = !isMoreThanOneBookedEntertainmentsOfThisCategory && !isOnlyOneEntertainmentBookedInThePast;
  const bookedEventsWithoutDining = bookedEvents.filter(({ type }) => type === entertainmentType);
  const needToRemoveTheOtherClashedEvent = preventOverlapMandatory && clashedEventsForTheSelectedEvent.length === 1;

  const [eveningRestrictionStartHour = '0', eveningRestrictionStartMinute = '0'] = eveningShowRestrictionStartTime.split(':');
  const [eveningRestrictionEndHour = '0', eveningRestrictionEndMinute = '0'] = eveningShowRestrictionEndTime.split(':');

  const allBookedEntertainmentsToday = allBookedEntertainments.filter(
    ({ Time, ActivityId }) => ActivityId === selectedActivityId && moment(Time).isSame(moment(activityDate), 'day'),
  );

  useEffect(() => {
    const mappedBookedSlots = getMappedBookedSlots(bookedEvents, bookedSlotsOfThisEvent);
    setChecked(prevChecked => (prevChecked.length === 0
      ? mappedBookedSlots
      : getArrayWithUniqueElements([...mappedBookedSlots, ...prevChecked])));
  }, [bookedSlotsOfThisEventString]); // eslint-disable-line

  return (
    <>
      {showReloadIsNecessaryDialog && (
        <Dialog
          messages={[`You've been inactive on the page for more than ${refreshTimeInterval} mins, your page will refreshed`]}
          positiveButtonText="OK"
          onPositiveButtonClick={redirectUserToSchedulePageWithReload}
          hideNegativeButton
        />
      )}
      {
        showGuestSelectorDialog && (
          <NewDialog
            onPositiveButtonClick={() => {
              setChecked([{ slotId: selectedSlotIdOfGuestSelector }]);
              setGuestSelectorDialogData(defaultGuestSelectorHookValue);
            }}
            onNegativeButtonClick={() => setGuestSelectorDialogData(defaultGuestSelectorHookValue)}
            negativeButtonText="CANCEL"
            positiveButtonText="OK"
            onBackArrowClick={() => setGuestSelectorDialogData(defaultGuestSelectorHookValue)}
          >
            <GuestSelector
              maxNumOfGuests={getMaxNumOfGuests(options, selectedSlotIdOfGuestSelector, maxQuantity)}
              isClashedEvent={clashedEventsForTheSelectedEvent.length > 0}
              setNumberOfGuests={setSelectedNumOfGuests}
              selectedNumberOfGuests={quantityToBook}
            />
          </NewDialog>
        )
      }
      {
        entertainmentDialogData.show && (
          <NewDialog
            positiveButtonText="OK"
            negativeButtonText="CANCEL"
            onPositiveButtonClick={() => onPositiveEntertainmentDialogButtonClick(
              showReplacePopup, setEntertainmentDialogData, saveAddon, setClashedEvents, entertainmentDialogData,
            )}
            onNegativeButtonClick={() => onNegativeEntertainmentDialogButtonClick(
              setEntertainmentDialogData, setClashedEvents,
            )}
            hideNegativeButton={!showReplacePopup}
            onBackArrowClick={() => onNegativeEntertainmentDialogButtonClick(
              setEntertainmentDialogData, setClashedEvents,
            )}
          >
            <DialogMessage
              messages={getEntertainmentDialogMessage(showReplacePopup, clashedEventsForTheSelectedEvent)}
              date={getRemovableEntertainmentDateLabel(bookedEvents, 'other', selectedActivityId, showReplacePopup)}
            />
          </NewDialog>
        )
      }
      {
        overlapsDialogData.show && (
          <Dialog
            messages={getClashedDialogMessage(clashedEventsForTheSelectedEvent, preventOverlapMandatory)}
            positiveButtonText={needToRemoveTheOtherClashedEvent ? 'YES' : 'OK'}
            negativeButtonText={needToRemoveTheOtherClashedEvent ? 'NO' : 'CANCEL'}
            onPositiveButtonClick={() => onPositiveClashedDialogButtonClick(
              preventOverlapMandatory,
              setClashedEvents,
              setOverlapsDialogData,
              clashedEventsForTheSelectedEvent,
              needToRemoveTheOtherClashedEvent,
              removeClashedEvent,
              setChecked,
              checked,
              !(alreadyBookedEntertainmentsNumber < maxAllowedShowBookingSlots),
              setEntertainmentDialogData,
              overlapsDialogData,
              isPerPersonActivity,
              setGuestSelectorDialogData,
              getBookedEntertainments,
              resId,
              siteCode,
            )}
            onNegativeButtonClick={() => onNegativeClashedDialogButtonClick(
              setClashedEvents,
              setOverlapsDialogData,
              setChecked,
              checked,
              preventOverlapMandatory,
              clashedEventsForTheSelectedEvent,
            )}
            hideNegativeButton={clashedEventsForTheSelectedEvent.length !== 1}
          />
        )
      }
      {
        isPerPersonActivity && (
          <div className="sub-title-perperson">
            Please note when booking this venue, <span className="bold">a table is not guaranteed. </span>
            You will either be sat on a table with up to five guests (note, this could be with other guests outside of your party) or in theatre style seating.
          </div>
        )
      }
      <div className="title-container">
        <div className="options-title">{title}</div>
      </div>
      <div className="options-container">
        {!!options && options.filter(option => filterSlotsByBackstop(option, backstopDate)).map((option, index) => {
          const { code, remainingCapacity = 1, singleDate, duration = 0 } = option;
          const isChecked = getIsChecked(checked, code);
          const areSpacesAvailable = remainingCapacity !== 0 && quantityToBook <= remainingCapacity;
          const isAlreadyBooked = bookedSlotsOfThisEvent.includes(code);
          const bookedEventsOnThisDay = getEventsOnThisDay(bookedEventsWithoutDining, singleDate);
          const numOfBookedEveningRestrictedActivities = getNumOfBookedEveningShowsOnCertainDay(
            allBookedEntertainmentsToday,
            singleDate,
            eveningRestrictionStartHour,
            eveningRestrictionStartMinute,
            eveningRestrictionEndHour,
            eveningRestrictionEndMinute,
          );
          const isAllowedToBookEveningShow = numOfBookedEveningRestrictedActivities === 0
            ? getIfAllowedToBookEveningShowSlot(
              option,
              allBookedEntertainments,
              isItTargetCategoryForEveningShow,
              eveningShowRestrictionStartDate,
              eveningShowRestrictionEndDate,
              eveningShowRestrictionStartTime,
              eveningShowRestrictionEndTime,
              additionalEveningShowTime,
            ) : true;
          const clashedEvents = getClashedBookedEntertainments(
            singleDate,
            duration,
            code,
            bookedEventsOnThisDay,
            overlapCheckPoolItems,
            overlapAllowedMinutes,
          );
          const isClashedEvent = clashedEvents.length > 0;
          const disabled = getOptionDisabled(
            isChecked,
            areSpacesAvailable,
            singleDate,
            duration,
            isAlreadyBooked,
            isAllowedToBookEveningShow,
          );
          return (
            <div key={index}>
              <EntertainmentOption
                key={index}
                label={getDateLabel(option)}
                checked={isChecked}
                color={getSlotColor(index)}
                isClashedEvent={isClashedEvent && preventOverlapMandatory}
                disabled={disabled}
                onOptionClick={() => onOptionClick(
                  setChecked,
                  disabled,
                  code,
                  setEntertainmentDialogData,
                  checked,
                  singleDate,
                  duration,
                  alreadyBookedEntertainmentsNumber,
                  maxAllowedShowBookingSlots,
                  allowLateBooking,
                  clashedEvents,
                  setClashedEvents,
                  setOverlapsDialogData,
                  lateBookingWindowMinutes,
                  preventOverlapMandatory,
                  setGuestSelectorDialogData,
                  isPerPersonActivity,
                  clashedEventsForTheSelectedEvent,
                  showReplacePopup,
                )}
                areSpacesAvailable={areSpacesAvailable}
                isAlreadyBooked={isAlreadyBooked}
                isAllowedToBookEveningShow={isAllowedToBookEveningShow}
              />
              {index < optionsLength - 1 && <Separator label={separatorLabel} />}
            </div>
          );
        })}
      </div>
      <div className="book-time-btn">
        <Button
          text="Book time"
          disabled={getIsBookButtonDisabled(checked,
            bookedSlotsOfThisEvent,
            isChristmasEventBooked,
            isRestrictedShowBooked
          )}
          fontSize={14}
          onClick={() => onBookButton(
            refreshTimeInterval,
            lastConfigCheckDateTime,
            saveAddon,
            checked,
            setShowReloadIsNecessaryDialog,
          )}
        />
      </div>
      {
        getShowCancelButton(bookedSlotsOfThisEvent, options) && (
          <div
            className="cancel-booking-container"
            onClick={() => handleCancel(
              setChecked,
              removeAddon,
              bookedSlotsOfThisEvent,
              checked,
            )}
          >
            {checked.length > 0 && (
              <div className="cancel-booking">Cancel booking</div>
            )}
          </div>
        )
      }
    </>
  );
};

EntertainmentOptions.propTypes = {
  title: PropTypes.string,
  separatorLabel: PropTypes.string,
  options: PropTypes.array,
  saveAddon: PropTypes.func,
  removeAddon: PropTypes.func,
  removeClashedEvent: PropTypes.func,
  bookedSlotsOfThisEvent: PropTypes.array,
  maxAllowedShowBookingSlots: PropTypes.number,
  bookedEvents: PropTypes.array,
  quantityToBook: PropTypes.number,
  allBookedEntertainments: PropTypes.array,
  lateBookingWindowMinutes: PropTypes.number,
  overlapCheckPoolItems: PropTypes.bool,
  overlapAllowedMinutes: PropTypes.number,
  allowLateBooking: PropTypes.bool,
  preventOverlapMandatory: PropTypes.bool,
  xmasCollectionActId: PropTypes.number,
  xmasExperienceActId: PropTypes.number,
  selectedEntertainment: PropTypes.object,
  isItTargetCategoryForEveningShow: PropTypes.bool,
  eveningShowRestrictionStartDate: PropTypes.string,
  eveningShowRestrictionEndDate: PropTypes.string,
  eveningShowRestrictionStartTime: PropTypes.string,
  eveningShowRestrictionEndTime: PropTypes.string,
  additionalEveningShowTime: PropTypes.string,
  refreshTimeInterval: PropTypes.number,
  lastConfigCheckDateTime: PropTypes.string,
  isPerPersonActivity: PropTypes.bool,
  maxQuantity: PropTypes.number,
  setSelectedNumOfGuests: PropTypes.func,
  backstopDate: PropTypes.string,
  resId: PropTypes.number,
  siteCode: PropTypes.string,
  getBookedEntertainments: PropTypes.func,
  showRestrictId1: PropTypes.number,
  showRestrictId2: PropTypes.number,
};

export default EntertainmentOptions;
